import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ASSETS } from "@/data/assets";
import { SectionHeading } from "@/components/ui-bits";
import { MapPin, Target, Users, Award } from "lucide-react";
import api from "@/lib/apiClient";

const PageHero = ({ overline, title, img }) => (
  <section className="relative h-[46vh] min-h-[340px] flex items-end">
    <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-12 w-full">
      <p className="overline mb-3">{overline}</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">{title}</h1>
    </div>
  </section>
);

export default function About() {
  const { data: content = {} } = useQuery({ queryKey: ["site-content"], queryFn: async () => (await api.get("/site-content")).data });
  return (
    <div data-testid="about-page">
      <PageHero overline="About Us" title="The team behind your celebrations" img={ASSETS.couple} />

      <section className="py-24 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <SectionHeading overline="Our Story" title="Rooted in Jammu, celebrating across India." />
          <div className="mt-6 space-y-4 text-brand-ink/75 leading-relaxed">
            {(content.about_story
              ? content.about_story.split("\n").filter((p) => p.trim())
              : [
                  "WEDDING EVENTS was born from a simple belief — that every celebration deserves to be effortless for the people living it. What began as a small team of passionate planners in Jammu has grown into a full-service event & wedding management agency trusted for weddings, social milestones and corporate gatherings alike.",
                  "We position ourselves as a true One Stop Solution: venue, decor, catering, hospitality, photography, entertainment and logistics, all coordinated by one dedicated team so you never have to chase a dozen vendors.",
                  "We plan weddings of every tradition — Kashmiri Pandit, Gujarati, Punjabi, South Indian and more — alongside corporate events, destination celebrations and joyful social milestones, honouring every ritual with warmth and precision.",
                ]
            ).map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
        <div className="relative">
          <img src={ASSETS.chandelier} alt="Elegant event decor" className="w-full h-[440px] object-cover rounded-sm" />
        </div>
      </section>

      <section className="pb-24 max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-6">
        {[
          { icon: Target, title: "Our Mission", text: "To transform milestones into meaningful, beautifully executed experiences — stress-free for our clients." },
          { icon: Users, title: "Team Ethos", text: "Detail-obsessed, warm and dependable. We treat every event as if it were our own." },
          { icon: Award, title: "Our Expertise", text: "Weddings, destination weddings, social celebrations, and corporate productions of every scale." },
        ].map((c) => (
          <div key={c.title} className="border border-brand-navy/10 p-8 rounded-sm bg-white hover:-translate-y-1 transition-transform">
            <c.icon className="text-brand-gold" size={32} />
            <h3 className="font-serif text-2xl text-brand-navy mt-5">{c.title}</h3>
            <p className="mt-3 text-brand-ink/70 text-sm leading-relaxed">{c.text}</p>
          </div>
        ))}
      </section>

      <section className="py-20 bg-brand-navy relative grain">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <MapPin className="text-brand-gold mx-auto" size={34} />
          <h2 className="font-serif text-3xl sm:text-4xl text-white mt-5">Geographic Reach</h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto leading-relaxed">
            Headquartered in Jammu, we plan and execute events across Jammu & Kashmir and pan-India — including
            destination weddings in Rajasthan, Goa, Himachal, Kerala and beyond.
          </p>
          <Link to="/booking" className="mt-8 inline-flex bg-brand-gold text-brand-navy font-semibold px-8 py-3.5 rounded-sm hover:-translate-y-0.5 transition-transform">
            Plan With Us
          </Link>
        </div>
      </section>
    </div>
  );
}
